import { Injector, inject, makeEnvironmentProviders } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { MemberProfilesService } from '@admin/src/core/services/member-profiles.service';
import { environment } from '@admin/src/environments/environment';

import { provideChatState } from '@lib/chat/core/providers/chat-state.provider';
import { ChatCommunicationService } from '@lib/chat/core/services/chat-communication.service';
import { CHAT_NAVIGATION_HANDLERS, type ChatNavigationHandlers } from '@lib/chat/core/tokens/chat-navigation.tokens';
import { CHAT_API_URL, CHAT_LIB_CONFIG } from '@lib/chat/core/tokens/chat.tokens';
import { ChatLibConfig, ThreadData } from '@lib/chat/models/chat-config.interface';
import { ThreadType } from '@lib/chat/models/chat.interface';

import { MemberProfile } from '@lib/core/models/member.interface';

export function provideAdminChatConfig() {
  return makeEnvironmentProviders([
    { provide: CHAT_API_URL, useValue: environment.protectedApiUrl },
    {
      provide: CHAT_LIB_CONFIG,
      useFactory: (): ChatLibConfig => {
        const injector = inject(Injector);

        return {
          ownUserInfo: {
            userId: '00000000-0000-0000-0000-000000000000', // TODO: Get from admin user store when available
            senderName: 'Команда підтримки', // TODO: Get from admin user store when available
          },
          includeThreadTypes: [ThreadType.SupportChannel],
          features: {
            showThreadTabs: false, // Disable thread tabs and assignee functionality
            showMedicalHistory: false,
            showPrescriptionsTab: false,
            showMemberProfile: true, // Enable side panel for client profile
            showHealthCheckScheduler: false, // Hidden for admin support chat
            showMemberComments: false, // Hidden for admin support chat
            enableProfileNavigation: false, // No navigation from admin chat
            enableAppointmentCreation: false,
          },
          notificationConfig: {
            title: 'Нове повідомлення в адмін панелі',
            icon: 'dist/assets/images/logo.svg',
            soundEnabled: true,
          },
          resolveThreadData: async (threadId: string) => {
            try {
              // Use captured injector
              const chatApiService = injector.get(ChatCommunicationService);
              const memberProfilesService = injector.get(MemberProfilesService);
              const thread = await firstValueFrom(chatApiService.getThread(threadId));

              let memberProfile: MemberProfile | null = null;

              // Try to load member profile for the thread owner
              try {
                memberProfile = await firstValueFrom(memberProfilesService.getClientProfileByUserId(thread.ownerId));
              } catch (profileError) {
                console.warn('Could not load member profile for admin chat:', profileError);
              }

              return {
                thread,
                profile: memberProfile,
                relatedItems: [],
              } as ThreadData;
            } catch (error) {
              console.error('Error resolving thread data:', error);
              throw error;
            }
          },
        };
      },
      deps: [],
    },
    {
      provide: CHAT_NAVIGATION_HANDLERS,
      useFactory: (): ChatNavigationHandlers => ({
        navigateToPrescription: (prescriptionId: string, userId: string) => {
          console.log('Admin chat: prescription navigation not implemented', { prescriptionId, userId });
        },

        switchToPrescriptionTab: (prescriptionId: string) => {
          console.log('Admin chat: prescription tab switching not implemented', { prescriptionId });
        },

        openCreateAppointmentModal: (userId: string, modalType: any, metadata?: any) => {
          console.log('Admin chat: appointment creation not implemented', { userId, modalType, metadata });
        },

        navigateToMemberProfile: (userId: string) => {
          console.log('Admin chat: member profile navigation not implemented', { userId });
        },
      }),
      deps: [],
    },
    provideChatState(),
  ]);
}
